import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import {
  AppBar,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { ArrowLeft, Save, Trash, X } from 'react-feather';
import { useAnchorElState } from 'react-use-object-state';
import useSharedUsersAndAccountStyles from 'navbar/src/jupiterAccountSettings/AccountSettings/useSharedUsersAndAccountStyles';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    appBar: {
      background: 'none',
      boxShadow: 'none',
      padding: '22px 20px',
      position: 'relative',
    },
    closeGroupDetails: {
      marginRight: -12,
      marginTop: -12,
    },
    detailTooltipContainer: {
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    groupAvatarTitle: {
      margin: '0 auto 4px',
      height: 72,
      width: 72,
    },
    groupDetailsPaper: {
      overflowX: 'auto',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'unset',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      '&::-webkit-scrollbar': {
        background: `rgba(8, 34, 76, .875)`,
        borderRadius: '0 4px 4px 0',
      },
      '&::-webkit-scrollbar-thumb': {
        background: `rgba(0, 6, 42, .875)`,
        borderRadius: '0 4px 4px 0',
      },
      '&:before': {
        background: theme.palette.primary.dark,
        borderRadius: '0 4px 0 0',
        display: 'block',
        content: `''`,
        position: 'absolute',
        height: 8,
        width: '100%',
        top: 0,
        left: 0,
      },
      borderRadius: '0 0 4px 4px',
      '& $header': {},
      '& $form': {
        margin: '20px 40px 40px',
      },
      animation: 'fadeIn',
      animationDuration: '0.35s',
    },
    avatarTitleContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: -theme.spacing(7),
      '& h2': {
        textAlign: 'center',
        fontWeight: 900,
        marginTop: 8,
      },
    },
    groupDetailsToolbar: {
      position: 'relative',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: 16,
      borderTopRightRadius: 4,
    },
    backButton: {
      marginLeft: -12,
      marginTop: -12,
    },
    actionButton: {
      color: theme.palette.primary.contrastText,
      '&:hover, &:focus': {
        background: theme.palette.primary.main,
      },
    },
  })
);

interface Props {
  children?: JSX.Element;
  handleBackButton: any;
  titleText: string;
  titleIcon: JSX.Element;
  onDeleteItem?: () => void;
  onSaveItem?: () => void;
}

const EditHeader = ({
  children,
  handleBackButton,
  titleIcon,
  titleText,
  onDeleteItem,
  onSaveItem,
}: Props) => {
  const classes = useStyles();
  const sharedClasses = useSharedUsersAndAccountStyles();

  const { anchorEl, setAnchorEl, clearAnchorEl } = useAnchorElState(null);

  const handleDeleteItem = () => {
    if (onDeleteItem) {
      onDeleteItem();
    }
  };

  const handleSaveItem = () => {
    if (onSaveItem) {
      onSaveItem();
    }
  };

  return (
    <Paper className={classes.groupDetailsPaper}>
      <AppBar className={classes.appBar} position="static">
        <Toolbar className={classes.groupDetailsToolbar}>
          <Tooltip title="Back" enterDelay={500} placement="top">
            <IconButton
              onClick={handleBackButton}
              className={clsx(classes.backButton, classes.actionButton)}
              color="inherit"
              aria-label="Back"
            >
              <ArrowLeft />
            </IconButton>
          </Tooltip>
          <div className={classes.detailTooltipContainer}>
            {onSaveItem && (
              <Tooltip title="Save" enterDelay={500} placement="top">
                <IconButton
                  className={clsx(
                    classes.actionButton,
                    classes.closeGroupDetails
                  )}
                  color="inherit"
                  onClick={handleSaveItem}
                  aria-label="Save"
                >
                  <Save />
                </IconButton>
              </Tooltip>
            )}
            {onDeleteItem && (
              <Tooltip title="Delete" enterDelay={500} placement="top">
                <IconButton
                  className={clsx(
                    classes.closeGroupDetails,
                    classes.actionButton
                  )}
                  color="inherit"
                  onClick={setAnchorEl}
                  aria-label="Delete"
                >
                  <Trash />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </Toolbar>
        <div className={classes.avatarTitleContainer}>
          <Avatar
            className={clsx(
              sharedClasses.groupAvatar,
              classes.groupAvatarTitle
            )}
          >
            {titleIcon}
          </Avatar>
          <Typography variant="h6" color="inherit">
            {titleText}
          </Typography>
        </div>
      </AppBar>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={clearAnchorEl}
      >
        <MenuItem onClick={clearAnchorEl}>
          <X /> Cancel
        </MenuItem>
        <MenuItem onClick={handleDeleteItem}>
          <Trash /> Delete
        </MenuItem>
      </Menu>
      {children}
    </Paper>
  );
};

export default EditHeader;
